import type * as React from 'react'
import { useCallback, useState } from 'react'
import type { JSX } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import {
  Button,
  Combobox,
  IconButton,
  SettingsIcon,
  toast,
} from '@buffer-mono/popcorn'
import type {
  ComboboxContentProps,
  ComboboxAnchorProps,
} from '@buffer-mono/popcorn'

import {
  useHasOrganizationAction,
  useIsFreeUser,
} from '~publish/contexts/AccountContext'
import { useTags } from '~publish/hooks/useTags'
import { actions as modalActions } from '~publish/legacy/modals/reducer'

import { TagEditDialog } from '../TagEditDialog'

import { TagsComboboxView } from './TagsComboboxView'

interface TagsComboboxProps {
  selectedTags: string[]
  onChange: (tags: string[]) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
  limit?: number
  align?: ComboboxContentProps['align']
  side?: ComboboxContentProps['side']
  anchor?: ComboboxAnchorProps['virtualRef']
  showFooter?: boolean
  className?: string
}

export function TagsCombobox({
  open,
  selectedTags,
  onChange: setSelectedTags,
  onOpenChange,
  children,
  limit = 10,
  align,
  side,
  anchor,
  showFooter = true,
  className,
}: TagsComboboxProps): JSX.Element {
  const dispatch = useDispatch()
  // Tag mutations are gated on the organization `edit` action server-side.
  const canManageTags = useHasOrganizationAction('manageTags')
  const isFreeUser = useIsFreeUser()
  const { tags, hasCreatePermission, createTag } = useTags()
  const [openNewTagModal, setOpenNewTagModal] = useState(false)
  const [tagInfo, setTagInfo] = useState<{ name: string } | undefined>()

  const handleCreateTag = (name: string): void => {
    if (!hasCreatePermission) {
      if (!canManageTags) {
        toast.error(
          'Only admins can create tags. Please ask an admin in your organization to create this tag.',
        )
      } else if (isFreeUser) {
        dispatch(modalActions.showTagsLimitUpgradeModal())
      } else {
        toast.error(
          'You have reached your tag limit of 250, please consider removing some tags to add new ones.',
        )
      }
      return
    }
    setTagInfo({ name })
    setOpenNewTagModal(true)
  }

  const handleSaveTag = useCallback(
    async (tag: { name: string; color: string }): Promise<void> => {
      try {
        const { id } = await createTag(tag)

        setSelectedTags([...selectedTags, id])
        setOpenNewTagModal(false)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : String(error))
      }
    },
    [createTag, selectedTags, setSelectedTags],
  )

  return (
    <>
      <TagsComboboxView
        tags={tags}
        open={open}
        selectedTags={selectedTags}
        onChange={setSelectedTags}
        onOpenChange={onOpenChange}
        limit={limit}
        canCreate={canManageTags}
        onCreate={handleCreateTag}
        footer={
          showFooter && (
            <Combobox.Footer>
              <Combobox.Clear>
                <Button variant="tertiary">Clear All</Button>
              </Combobox.Clear>
              <IconButton
                label="Manage tags"
                tooltip="Manage tags"
                as={Link}
                to="/tags"
                variant="tertiary"
              >
                <SettingsIcon />
              </IconButton>
            </Combobox.Footer>
          )
        }
        align={align}
        side={side}
        anchor={anchor}
        className={className}
      >
        {children}
      </TagsComboboxView>

      <TagEditDialog
        onOpenChange={setOpenNewTagModal}
        open={openNewTagModal}
        tag={tagInfo}
        onSubmit={handleSaveTag}
      />
    </>
  )
}
